<template>
  <div class="layer-chart-settings">
    <!-- Nút thêm mới -->
    <div class="mb-3">
      <v-btn color="primary" @click="openCreateDialog">
        <v-icon left>mdi-plus</v-icon>
        Thêm Biểu Đồ
      </v-btn>
    </div>

    <!-- Bảng danh sách cấu hình đã tạo -->
    <v-data-table :headers="headers" :items="chartList" :loading="loadingList" class="elevation-1 border"
      disable-pagination hide-default-footer dense>
      <template v-slot:[`item.name`]="{ item }">
        <div class="font-weight-medium text-truncate" style="max-width: 170px;" :title="item.name">
          {{ item.name }}
        </div>
      </template>
      <template v-slot:[`item.type`]="{ item }">
        <v-chip :color="chartTypeTag(item.type)" x-small dark class="font-weight-bold">{{ chartTypeLabel(item.type)
          }}</v-chip>
      </template>
      <template v-slot:[`item.x_axis_field`]="{ item }">
        <span class="text-caption text-truncate d-inline-block" style="max-width: 110px;" :title="item.x_axis_field">
          {{ item.x_axis_field }}
        </span>
      </template>
      <template v-slot:[`item.formula`]="{ item }">
        <div class="text-truncate grey lighten-4 pa-1 rounded text-caption font-monospace border"
          style="max-width: 240px;" :title="item.custom_formula || (item.aggregation + '(' + item.y_axis_field + ')')">
          <span v-if="item.custom_formula" class="primary--text font-weight-medium">{{ item.custom_formula }}</span>
          <span v-else class="grey--text text--darken-3">{{ item.aggregation }}({{ item.y_axis_field }})</span>
        </div>
      </template>
      <template v-slot:[`item.is_active`]="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="d-inline-block">
              <v-switch v-model="item.is_active" dense hide-details color="success" class="mt-0 pt-0 d-inline-block"
                @change="toggleChartActive(item)"></v-switch>
            </div>
          </template>
          <span>{{ item.is_active ? 'Đang bật hiển thị' : 'Đang ẩn hiển thị' }}</span>
        </v-tooltip>
      </template>
      <template v-slot:[`item.action`]="{ item }">
        <div class="d-flex align-center justify-center">
          <v-btn icon x-small color="info" @click="$emit('preview', item.id)" title="Xem trước" class="mr-1">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
          <v-btn icon x-small color="primary" @click="openEditDialog(item)" title="Sửa" class="mr-1">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon x-small color="error" @click="handleDelete(item)" title="Xóa">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Dialog Form (Component con) -->
    <ChartSettingsForm
      v-model="dialogVisible"
      :layerId="layerId"
      :layerFields="layerFields"
      :isEdit="isEdit"
      :editId="editId"
      :initialData="editData"
      @saved="fetchChartList"
    />
  </div>
</template>

<script>
import { ConfirmBuilder } from '@utils'
import { sdk } from '@service/http/axios'
import ChartSettingsForm from './ChartSettingsForm.vue'

export default {
  name: 'LayerChartSettings',
  components: { ChartSettingsForm },
  props: {
    layerId: { type: [String, Number], required: true },
    datastoreDetailId: { type: [String, Number], default: null },
  },
  data() {
    return {
      chartList: [],
      layerFields: [],
      loadingList: false,
      dialogVisible: false,
      isEdit: false,
      editId: null,
      editData: {},
      headers: [
        { text: 'Tên Biểu Đồ', value: 'name', width: '170px' },
        { text: 'Loại', value: 'type', width: '85px', align: 'center' },
        { text: 'Trục Nhãn', value: 'x_axis_field', width: '120px' },
        { text: 'Trục Giá trị / Công thức', value: 'formula' },
        { text: 'Hiển thị', value: 'is_active', align: 'center', width: '90px', sortable: false },
        { text: 'Thao tác', value: 'action', align: 'center', width: '100px', sortable: false },
      ],
    }
  },
  mounted() {
    this.fetchChartList()
    this.fetchLayerFields()
  },
  methods: {
    async fetchChartList() {
      this.loadingList = true
      try {
        const { data } = await sdk.request({
          url: `layer/${this.layerId}/chart-configs`,
          method: 'get'
        })
        this.chartList = data.list || data.data || data || []
      } finally {
        this.loadingList = false
      }
    },

    async fetchLayerFields() {
      if (!this.layerId) return
      try {
        const { data } = await sdk.request({
          url: `layers/${this.layerId}/fields`,
          method: 'get'
        })
        this.layerFields = data.list || data.data || data || []
      } catch (e) {
        console.error('Lỗi tải danh sách trường:', e)
      }
    },

    openCreateDialog() {
      this.isEdit = false
      this.editId = null
      this.editData = {}
      this.dialogVisible = true
    },

    openEditDialog(row) {
      this.isEdit = true
      this.editId = row.id
      this.editData = { ...row }
      this.dialogVisible = true
    },

    async toggleChartActive(item) {
      try {
        await sdk.request({
          url: `layer/${this.layerId}/chart-configs/${item.id}`,
          method: 'put',
          data: { is_active: item.is_active }
        })
        this.$store.dispatch('notify/success', {
          message: item.is_active ? 'Đã bật hiển thị biểu đồ' : 'Đã ẩn hiển thị biểu đồ'
        })
      } catch (e) {
        item.is_active = !item.is_active // Rollback nếu lỗi
        this.$store.dispatch('notify/error', { message: 'Không thể cập nhật trạng thái' })
      }
    },

    async handleDelete(row) {
      let confirm = new ConfirmBuilder()
      confirm
        .addMessage(`Bạn có chắc chắn muốn xóa biểu đồ "${row.name}"?`)
        .setTitle('Xác nhận xóa')
        .setType('delete')

      if (await confirm.confirm()) {
        try {
          await sdk.request({
            url: `layer/${this.layerId}/chart-configs/${row.id}`,
            method: 'delete'
          })
          this.$store.dispatch('notify/success', { message: 'Đã xóa cấu hình biểu đồ thành công' })
          this.fetchChartList()
        } catch (e) {
          this.$store.dispatch('notify/error', { message: 'Xóa cấu hình thất bại' })
        }
      }
    },

    chartTypeLabel(type) {
      const map = { line: 'Đường', bar: 'Cột', pie: 'Tròn', doughnut: 'Vành khuyên' }
      return map[type] || type
    },

    chartTypeTag(type) {
      const map = { line: 'primary', bar: 'success', pie: 'warning', doughnut: 'error' }
      return map[type] || 'primary'
    },
  },
}
</script>

<style scoped>
.layer-chart-settings ::v-deep .v-data-table th,
.layer-chart-settings ::v-deep .v-data-table td {
  padding: 0 4px !important;
}
</style>
